import { useCallback, useEffect, useMemo, useState } from 'react'
import debounce from 'lodash.debounce'
import { useHttpsCallable } from 'react-firebase-hooks/functions'
import { firebase_functions } from '../../../common/firebaseConfig'
import { AlertCardTrade, AlertTradeSummaryStats } from '../types/AlertHistoryItem'

export type AlertTradeSummary = {
  stats: AlertTradeSummaryStats
  tradesByNotificationId: Record<string, AlertCardTrade>
}

type TradeSummariesResponse = {
  status: number
  data?: Record<string, AlertTradeSummary>
}

// Server-side cap on alerts per call
const BATCH_SIZE = 50
const REFRESH_DEBOUNCE_MS = 1500

const chunk = <T>(items: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(items.length / size) }, (_, i) =>
    items.slice(i * size, (i + 1) * size)
  )

// One batched fetch of trade stats + recent trades for every auto-trade alert in the list.
// `refresh` is debounced so several cards noticing a new fire at once trigger a single call.
export function useAlertTradeSummaries(alertIds: string[]) {
  const [getAlertTradeSummaries] = useHttpsCallable<{ alertIds: string[] }, TradeSummariesResponse>(
    firebase_functions,
    'getAlertTradeSummaries'
  )

  const [summaries, setSummaries] = useState<Record<string, AlertTradeSummary>>({})
  const [loading, setLoading] = useState(false)

  const idsKey = useMemo(() => [...alertIds].sort().join(','), [alertIds])

  const fetchSummaries = useCallback(async () => {
    const ids = idsKey ? idsKey.split(',') : []
    if (ids.length === 0) {
      setSummaries({})
      return
    }
    setLoading(true)
    try {
      const results = await Promise.all(
        chunk(ids, BATCH_SIZE).map((batch) => getAlertTradeSummaries({ alertIds: batch }))
      )
      const merged: Record<string, AlertTradeSummary> = {}
      let anyChunkSucceeded = false
      results.forEach((result) => {
        if (result?.data?.status === 200 && result.data.data) {
          anyChunkSucceeded = true
          Object.assign(merged, result.data.data)
        }
      })
      // useHttpsCallable resolves undefined on failure; keep the last good stats rather than
      // wiping every card back to dashes on a transient error. Rebuilding from the current id set
      // drops entries for alerts that no longer have a trading account.
      if (anyChunkSucceeded) {
        setSummaries((prev) => {
          const next: Record<string, AlertTradeSummary> = {}
          ids.forEach((id) => {
            const summary = merged[id] ?? prev[id]
            if (summary) next[id] = summary
          })
          return next
        })
      }
    } catch (err) {
      console.error('Error fetching alert trade summaries:', err)
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey])

  useEffect(() => {
    fetchSummaries()
  }, [fetchSummaries])

  const refresh = useMemo(() => debounce(fetchSummaries, REFRESH_DEBOUNCE_MS), [fetchSummaries])
  useEffect(() => () => refresh.cancel(), [refresh])

  return { summaries, loading, refresh }
}
